import React, { useEffect, useMemo, useState } from 'react';
import { Page, NotifyModal } from 'components';
import Header from 'components/Header/Header';
import Reload from '../../components/Reload/Reload';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import { FaKey, FaShoppingCart } from 'react-icons/fa';
import decodeJwt from '../../hooks/decode';
import { KPI, getDashboardCardSurface } from 'constants/dashboardColors';
import { VolumeDiscountTable, PurchaseSummary } from './components';
import {
  ADVANCE_PAYMENT_OPTIONS,
  CUSTOM_QUANTITY_OPTION,
  VOLUME_DISCOUNT_TIERS,
} from './assistenciasComprarSenhasConfig';
import {
  calculatePurchaseQuote,
  createMoneyFormatter,
  getUnitKeyPrice,
  hasSenhasReadPermission,
  hasSenhasWritePermission,
  resolveVolumeTier,
} from './assistenciasComprarSenhasUtils';
import {
  comprarSenhasAssistencia,
  loadAssistenciaForLoggedUser,
} from './assistenciasComprarSenhasApi';
import './AssistenciasComprarSenhas.css';

const DEFAULT_QUANTITY = VOLUME_DISCOUNT_TIERS[0]?.quantity ?? 50;

const AssistenciasComprarSenhas = () => {
  const user = JSON.parse(localStorage.getItem('userData') || 'null');
  const jwtPayload = decodeJwt();

  if (!hasSenhasReadPermission(user)) {
    window.location.replace('/home');
  }

  const canPurchase = hasSenhasWritePermission(user);

  const [assistencia, setAssistencia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [selectedQuantity, setSelectedQuantity] = useState(DEFAULT_QUANTITY);
  const [quantityMode, setQuantityMode] = useState('preset');
  const [customQuantity, setCustomQuantity] = useState('');
  const [advanceOptionId, setAdvanceOptionId] = useState(ADVANCE_PAYMENT_OPTIONS[0].id);

  const [type, setType] = useState({ color: '', status: '' });
  const [modalShow, setModalShow] = useState(false);

  const cardSurface = getDashboardCardSurface();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setLoadError('');
        const data = await loadAssistenciaForLoggedUser(user, jwtPayload);
        if (!data) {
          setLoadError('Não foi possível identificar a assistência vinculada ao seu usuário.');
          setAssistencia(null);
          return;
        }
        setAssistencia(data);
      } catch (error) {
        console.error('Erro ao carregar assistência:', error);
        setLoadError('Falha ao carregar dados da assistência. Tente novamente.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const formatMoney = useMemo(
    () => (assistencia ? createMoneyFormatter(assistencia) : (v) => String(v)),
    [assistencia]
  );

  const unitPrice = assistencia ? getUnitKeyPrice(assistencia) : 0;

  const effectiveQuantity = useMemo(() => {
    if (quantityMode === CUSTOM_QUANTITY_OPTION) {
      const parsed = parseInt(customQuantity, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }
    return selectedQuantity;
  }, [quantityMode, customQuantity, selectedQuantity]);

  const volumeTier = useMemo(
    () => resolveVolumeTier(effectiveQuantity),
    [effectiveQuantity]
  );

  const advanceOption = useMemo(
    () => ADVANCE_PAYMENT_OPTIONS.find((o) => o.id === advanceOptionId) || ADVANCE_PAYMENT_OPTIONS[0],
    [advanceOptionId]
  );

  const quote = useMemo(
    () =>
      calculatePurchaseQuote({
        quantity: effectiveQuantity,
        unitPrice,
        volumeDiscountPercent: volumeTier.volumeDiscountPercent,
        advanceDiscountPercent: advanceOption.advanceDiscountPercent,
        advanceMonths: advanceOption.months,
      }),
    [effectiveQuantity, unitPrice, volumeTier, advanceOption]
  );

  const handleAlertSuccess = (msg) => {
    setType({ color: '#198754', status: msg });
    setModalShow(true);
  };

  const handleAlertError = (msg) => {
    setType({ color: 'red', status: msg });
    setModalShow(true);
  };

  const handlePurchase = async () => {
    if (!canPurchase) {
      handleAlertError('Você não tem permissão para realizar compras de senhas.');
      return;
    }
    if (!assistencia?._id) {
      handleAlertError('Assistência não carregada.');
      return;
    }
    if (effectiveQuantity < 1) {
      handleAlertError('Informe uma quantidade válida de chaves.');
      return;
    }

    try {
      setSubmitting(true);
      await comprarSenhasAssistencia({
        assistenciaId: assistencia._id,
        quantity: effectiveQuantity,
        volumeTierQuantity: volumeTier.quantity,
        volumeDiscountPercent: volumeTier.volumeDiscountPercent,
        advancePaymentMonths: advanceOption.months,
        advanceDiscountPercent: advanceOption.advanceDiscountPercent,
        unitPrice,
        totalAmount: quote.total,
        currency: assistencia.currency || 'BRL',
      });
      handleAlertSuccess(
        `Pedido de ${effectiveQuantity} chave(s) registrado com sucesso! Total: ${formatMoney(quote.total)}`
      );
    } catch (error) {
      console.error('Erro ao comprar senhas:', error);
      const apiMsg = error?.response?.data?.message || error?.response?.data;
      handleAlertError(
        typeof apiMsg === 'string' && apiMsg
          ? apiMsg
          : 'Não foi possível concluir a compra. Verifique se o endpoint de compra está disponível no servidor.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page className="p-3" title="Comprar Senhas">
      <Header page="Comprar Senhas" />

      <NotifyModal type={type} show={modalShow} onHide={() => setModalShow(false)} />

      {loading && <Reload />}

      {loadError && (
        <Alert variant="danger" className="mt-3">
          {loadError}
        </Alert>
      )}

      {!loading && assistencia && (
        <>
          <Alert variant="info" className="mt-3 py-2">
            <strong>{assistencia.nome}</strong>
            {' · '}
            {assistencia.cidade}/{assistencia.estado}
            {' · '}
            Preço base por chave: <strong>{formatMoney(unitPrice)}</strong>
            {assistencia.desconto != null && (
              <Badge bg="success" className="ms-2">
                Desconto cadastral aplicado
              </Badge>
            )}
          </Alert>

          <Row className="g-3 mt-1">
            <Col lg={7}>
              <Card className="comprar-senhas-panel border-0" style={cardSurface}>
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <FaKey style={{ color: KPI.primary }} />
                    <h5 className="mb-0 fw-bold">Tabela de descontos por volume</h5>
                  </div>
                  <p className="text-muted small mb-3">
                    Selecione um pacote pré-definido. Quantidades personalizadas recebem o desconto
                    da maior faixa atingida (50, 100, 500 ou 1000 chaves).
                  </p>

                  <VolumeDiscountTable
                    tiers={VOLUME_DISCOUNT_TIERS}
                    selectedQuantity={quantityMode === 'preset' ? selectedQuantity : null}
                    unitPrice={unitPrice}
                    formatMoney={formatMoney}
                    disabled={quantityMode === CUSTOM_QUANTITY_OPTION}
                    onSelectTier={(qty) => {
                      setQuantityMode('preset');
                      setSelectedQuantity(qty);
                    }}
                  />

                  <Form className="mt-3">
                    <Form.Check
                      type="radio"
                      id="qty-custom"
                      name="quantityMode"
                      label="Outra quantidade"
                      checked={quantityMode === CUSTOM_QUANTITY_OPTION}
                      onChange={() => setQuantityMode(CUSTOM_QUANTITY_OPTION)}
                      className="mb-2"
                    />
                    {quantityMode === CUSTOM_QUANTITY_OPTION && (
                      <Row className="g-2 align-items-end">
                        <Col sm={6} md={4}>
                          <Form.Group>
                            <Form.Label>Quantidade de chaves</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              step={1}
                              value={customQuantity}
                              onChange={(e) => setCustomQuantity(e.target.value)}
                              placeholder="Ex: 250"
                            />
                          </Form.Group>
                        </Col>
                        <Col>
                          {effectiveQuantity > 0 && (
                            <small className="text-muted">
                              Faixa aplicada: <strong>{volumeTier.label}</strong>
                              {' '}({volumeTier.volumeDiscountPercent}% de desconto)
                            </small>
                          )}
                        </Col>
                      </Row>
                    )}
                  </Form>
                </Card.Body>
              </Card>

              <Card className="comprar-senhas-panel border-0 mt-3" style={cardSurface}>
                <Card.Body>
                  <h5 className="fw-bold mb-3">Desconto por antecipação</h5>
                  <p className="text-muted small mb-3">
                    Compre créditos de chaves com antecedência e ganhe desconto adicional sobre o
                    valor já reduzido pelo volume.
                  </p>
                  <Row className="g-2">
                    {ADVANCE_PAYMENT_OPTIONS.map((option) => (
                      <Col key={option.id} xs={12} sm={6}>
                        <Card
                          className={`comprar-senhas-tier-card h-100 ${
                            advanceOptionId === option.id ? 'selected' : ''
                          }`}
                          onClick={() => setAdvanceOptionId(option.id)}
                          style={cardSurface}
                        >
                          <Card.Body className="py-3">
                            <Form.Check
                              type="radio"
                              id={`advance-${option.id}`}
                              name="advanceOption"
                              label={option.label}
                              checked={advanceOptionId === option.id}
                              onChange={() => setAdvanceOptionId(option.id)}
                              className="fw-semibold"
                            />
                            <Badge
                              bg={option.advanceDiscountPercent > 0 ? 'success' : 'secondary'}
                              className="mt-2"
                            >
                              {option.advanceDiscountPercent}% antecipação
                            </Badge>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card
                className="comprar-senhas-panel border-0 sticky-top"
                style={{ ...cardSurface, top: '1rem', borderTop: `4px solid ${KPI.primary}` }}
              >
                <Card.Body>
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <FaShoppingCart style={{ color: KPI.primary }} />
                    <h5 className="mb-0 fw-bold">Resumo da compra</h5>
                  </div>

                  {effectiveQuantity < 1 ? (
                    <p className="text-muted mb-0">Selecione ou informe a quantidade de chaves.</p>
                  ) : (
                    <PurchaseSummary
                      quote={quote}
                      formatMoney={formatMoney}
                      volumeTier={volumeTier}
                      advanceOption={advanceOption}
                    />
                  )}

                  <Button
                    variant="success"
                    className="w-100 mt-4 d-flex align-items-center justify-content-center gap-2"
                    disabled={
                      !canPurchase || submitting || effectiveQuantity < 1 || !assistencia
                    }
                    onClick={handlePurchase}
                  >
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        Processando...
                      </>
                    ) : (
                      <>
                        <FaShoppingCart />
                        Confirmar compra
                      </>
                    )}
                  </Button>

                  {!canPurchase && (
                    <p className="text-muted small text-center mt-2 mb-0">
                      Permissão de edição em &quot;Senhas&quot; necessária para comprar.
                    </p>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Page>
  );
};

export default AssistenciasComprarSenhas;
